import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import {
  humanReady, humanRefusal, humanSiteKey, loadTurnstile, setHumanReset, setHumanToken, subscribeHuman, type HumanAction,
} from "../auth/human";

/**
 * The Cloudflare Turnstile widget for one way in (auth/human.ts). Put it
 * above the button that makes the gated call; the request builder takes the
 * token and resets the widget, so each call gets a fresh one.
 *
 * Renders nothing when config.turnstileSiteKey is unset.
 *
 * Look (owner 2026-10-24): Cloudflare draws the widget in its own frame, so its
 * background cannot be made transparent. Instead it takes the console's own
 * light/dark mode (not the browser's), shows only when Cloudflare needs a
 * click ("interaction-only"), sits centred, and drops to the compact size
 * when the card is narrower than the 300px the normal widget needs.
 */
export function HumanCheck({ action }: { action: HumanAction }) {
  const siteKey = humanSiteKey();
  const mode = useTheme().palette.mode;
  const box = useRef<HTMLDivElement>(null);
  const [loadError, setLoadError] = useState("");
  const snapshot = () => humanRefusal(action);
  const refused = useSyncExternalStore(subscribeHuman, snapshot, snapshot);
  const readySnapshot = () => humanReady(action);
  const ready = useSyncExternalStore(subscribeHuman, readySnapshot, readySnapshot);

  useEffect(() => {
    if (!siteKey) return;
    let id: string | null = null;
    let gone = false;
    loadTurnstile()
      .then((ts) => {
        if (gone || !box.current) return;
        id = ts.render(box.current, {
          sitekey: siteKey,
          action,
          theme: mode,
          size: box.current.clientWidth < 300 ? "compact" : "flexible",
          appearance: "interaction-only",
          callback: (t) => setHumanToken(action, t),
          "expired-callback": () => setHumanToken(action, ""),
          "error-callback": () => setHumanToken(action, ""),
        });
        setHumanReset(action, () => {
          if (id && window.turnstile) window.turnstile.reset(id);
        });
      })
      .catch((e: unknown) => setLoadError(e instanceof Error ? e.message : String(e)));
    return () => {
      gone = true;
      setHumanReset(action, null);
      setHumanToken(action, "");
      if (id && window.turnstile) window.turnstile.remove(id);
    };
  }, [siteKey, action, mode]);

  if (!siteKey) return null;
  return (
    <Box data-testid={`human-check-${action}`} sx={{ width: "100%", maxWidth: "100%" }}>
      <Box ref={box} sx={{ display: "flex", justifyContent: "center", "& iframe": { maxWidth: "100%" } }} />
      {/* Owner 2026-10-25: the widget is invisible while Cloudflare checks; say so. */}
      {!ready && !loadError && (
        <Typography variant="caption" color="text.secondary" component="p" align="center" data-testid="human-check-waiting">
          Checking that you are human…
        </Typography>
      )}
      {loadError && <Alert severity="error" sx={{ mt: 1 }}>{loadError}</Alert>}
      {refused && <Alert severity="warning" sx={{ mt: 1 }}>{refused}</Alert>}
      {/* Owner 2026-10-25: say what protects the page, as Cashfree does for reCAPTCHA. */}
      <Typography variant="caption" color="text.secondary" component="p" align="center" sx={{ mt: 0.5 }} data-testid="human-check-notice">
        Protected by Cloudflare Turnstile. Cloudflare&apos;s{" "}
        <Link href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener">Privacy Policy</Link> and{" "}
        <Link href="https://www.cloudflare.com/website-terms/" target="_blank" rel="noopener">Terms of Use</Link> apply.
      </Typography>
    </Box>
  );
}
